import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTablesContext } from "../../../context/tablesContextProvider";
import { AestraPrimaryCareTable } from "../../../data-model/aestraPrimaryCareTable";
import { FilterValues } from "../../../data-model/filterValues";
import { TableNames } from "../../../enums/tableNames";
import { useAbortableFetch } from "../../../hooks/useAbortableFetch";
import { makePostCallForNextPagesDataFetch } from "../apis/makePostCallForNextPagesDataFetch";
import { processFilterData } from "../utils/processFilterData";
import { PrimaryCareFilterPayload, useFilterPayload } from "./useFilterPayload";
import { PrimaryCareTableState } from "./usePrimaryCareTableData";

export interface UseNextPageDataFetchReturn {
  endIndex: number;
  setEndIndex: React.Dispatch<React.SetStateAction<number>>;
}

export const useNextPageDataFetch = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  primaryCareState: PrimaryCareTableState,
  setPrimaryCareState: React.Dispatch<
    React.SetStateAction<PrimaryCareTableState>
  >,
  setTableData: React.Dispatch<React.SetStateAction<AestraPrimaryCareTable[]>>,
  dateFromStart: string,
  dateFromEnd: string,
  pageSize: number = 120,
): UseNextPageDataFetchReturn => {
  const { setTableDataForContext } = useTablesContext();
  const [endIndex, setEndIndex] = useState<number>(0);

  const { runFetch: runNextPageFetch } = useAbortableFetch(
    makePostCallForNextPagesDataFetch,
  );

  const lastRequestedPageRef = useRef<number>(0);

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    dateFromStart,
    dateFromEnd,
    pageSize,
  );

  const initialDataFetchingExecutedRef = useRef(initialDataFetchingExecuted);
  const setPrimaryCareStateRef = useRef(setPrimaryCareState);
  const setTableDataRef = useRef(setTableData);
  const setTableDataForContextRef = useRef(setTableDataForContext);

  useEffect(() => {
    initialDataFetchingExecutedRef.current = initialDataFetchingExecuted;
    setPrimaryCareStateRef.current = setPrimaryCareState;
    setTableDataRef.current = setTableData;
    setTableDataForContextRef.current = setTableDataForContext;
  }, [
    initialDataFetchingExecuted,
    setPrimaryCareState,
    setTableData,
    setTableDataForContext,
  ]);

  useEffect(() => {
    lastRequestedPageRef.current = 0;
  }, [validFilters, searchTerm, dateFromStart, dateFromEnd]);

  const shouldFetchMoreData = useMemo(() => {
    // If no data yet, don't fetch
    if (!primaryCareState.data?.details?.pagination) {
      return false;
    }

    const currentPageRowCount = primaryCareState.data.details.rows?.length ?? 0;
    const currentPage = primaryCareState.data.details.pagination.page ?? 1;
    const totalPages =
      primaryCareState.data.details.pagination.total_pages ?? 1;
    const totalCount =
      primaryCareState.data.details.pagination.total_count ?? 0;
    const nextPageToFetch = currentPage + 1;

    const previousPagesRows = (currentPage - 1) * pageSize;
    const cumulativeRowsLoaded = previousPagesRows + currentPageRowCount;

    // Don't fetch if we've already loaded all available data
    const hasMoreData = cumulativeRowsLoaded < totalCount;

    return (
      endIndex >= cumulativeRowsLoaded - 1 &&
      currentPage < totalPages &&
      hasMoreData &&
      !primaryCareState.loading &&
      !primaryCareState.showInlineLoader &&
      nextPageToFetch > lastRequestedPageRef.current
    );
  }, [
    endIndex,
    primaryCareState.data?.details?.rows?.length,
    primaryCareState.data?.details?.pagination,
    primaryCareState.loading,
    primaryCareState.showInlineLoader,
    pageSize,
  ]);

  const payloadFinal = useMemo<PrimaryCareFilterPayload>(() => {
    if (filterPayload === null) {
      const apiFilters = processFilterData(validFilters);

      const payload: PrimaryCareFilterPayload = {
        filters: apiFilters,
        page: 1,
        page_size: pageSize,
        request_id: uuidv4(),
        cmm_fields: ["cmm_status", "error_text", "cmm_result_key"],
      };

      // Add keyword if search term exists
      if (searchTerm && searchTerm.trim().length > 0) {
        payload.keyword = searchTerm.trim();
      }

      return payload;
    }
    return filterPayload;
  }, [
    filterPayload,
    validFilters,
    dateFromStart,
    dateFromEnd,
    searchTerm,
    pageSize,
  ]);

  const payloadFinalWithPage = useMemo<PrimaryCareFilterPayload>(() => {
    return {
      ...payloadFinal,
      page: (primaryCareState.data?.details?.pagination?.page ?? 0) + 1,
      request_id: uuidv4(),
    };
  }, [payloadFinal, primaryCareState.data?.details?.pagination?.page]);

  const currentPage = primaryCareState.data?.details?.pagination?.page ?? 1;

  const fetchNextPage = useCallback(() => {
    const nextPage = currentPage + 1;
    lastRequestedPageRef.current = nextPage;

    runNextPageFetch(
      payloadFinalWithPage,
      setPrimaryCareStateRef.current,
      setTableDataRef.current,
    );
    setTableDataForContextRef.current(TableNames.PHARMA_PRIMARY_CARE_ORDERS, {
      lastBatchFetched: nextPage,
    });
  }, [payloadFinalWithPage, currentPage, runNextPageFetch]);

  useEffect(() => {
    if (shouldFetchMoreData && initialDataFetchingExecutedRef.current) {
      fetchNextPage();
    }
  }, [shouldFetchMoreData, fetchNextPage]);

  return {
    endIndex,
    setEndIndex,
  };
};
